import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { Plus, Trash2, CreditCard, FileText } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, logoSrc, local } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { DataTable } from "@/components/DataTable";
import { PaywallGate } from "@/components/PaywallGate";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { money, fmtDate } from "@/lib/format";

export default function Billing() {
  const { org } = useAuth();
  const qc = useQueryClient();
  const cur = org?.currency || "NGN";
  const [open, setOpen] = useState(false);
  const [patientId, setPatientId] = useState("");
  const [items, setItems] = useState([{ description: "", amount: "" }]);
  const [params, setParams] = useSearchParams();

  const { data: invoices = [], isLoading } = useQuery({ queryKey: ["invoices"], queryFn: async () => (await api.get("/invoices")).data || [] });
  const { data: patients = [] } = useQuery({ queryKey: ["patients"], queryFn: async () => (await api.get("/patients")).data || [] });
  const { data: profile = {} } = useQuery({ queryKey: ["org-profile"], queryFn: async () => (await local.get("/org-profile/me")).data || {} });

  const total = items.reduce((s, i) => s + (Number(i.amount) || 0), 0);

  const createMut = useMutation({
    mutationFn: async () => {
      const payload = {
        patient_id: patientId,
        items: items.filter((i) => i.description).map((i) => ({ description: i.description, amount: Number(i.amount) || 0 })),
        total,
        status: "unpaid",
      };
      return (await api.post("/invoices", payload)).data;
    },
    onSuccess: () => { toast.success("Invoice created"); qc.invalidateQueries({ queryKey: ["invoices"] }); setOpen(false); setItems([{ description: "", amount: "" }]); setPatientId(""); },
    onError: (e) => toast.error(errMsg(e)),
  });

  const checkoutMut = useMutation({
    mutationFn: async (id) => (await local.post(`/pay/invoice/checkout`, { invoice_id: id, origin_url: `${window.location.origin}/app/billing` })).data,
    onSuccess: (d, id) => { if (d?.url) { sessionStorage.setItem("fhapas_pending_invoice", id); window.location.href = d.url; } else toast.success("Checkout ready"); },
    onError: (e) => toast.error(errMsg(e, "Could not start checkout. Please try again.")),
  });

  // Stripe return handler: mark paid on success, or show a clear retry prompt on cancel/decline (invoice stays unpaid).
  useEffect(() => {
    const cancelled = params.get("payment") === "cancelled";
    const sid = params.get("session_id");
    const pendingInv = sessionStorage.getItem("fhapas_pending_invoice") || "";
    const retry = pendingInv ? { label: "Try again", onClick: () => checkoutMut.mutate(pendingInv) } : undefined;
    if (cancelled) {
      sessionStorage.removeItem("fhapas_pending_invoice");
      setParams({}, { replace: true });
      toast.error("Payment was not completed", { description: "This invoice is still unpaid.", action: retry });
      return;
    }
    if (!sid) return;
    let tries = 0;
    const poll = async () => {
      tries += 1;
      try {
        const s = (await local.get(`/pay/status/${sid}`)).data;
        const paid = s?.payment_status === "paid" || s?.status === "completed";
        if (paid) {
          if (s?.invoice_id) { try { await api.put(`/invoices/${s.invoice_id}`, { status: "paid" }); } catch (_) {} }
          sessionStorage.removeItem("fhapas_pending_invoice");
          toast.success("Payment received — invoice marked paid", {
            description: "A branded PDF receipt has been emailed to the patient.",
            duration: 15000,
            action: s?.receipt_url ? { label: "Receipt", onClick: () => window.open(`${process.env.REACT_APP_BACKEND_URL}${s.receipt_url}`, "_blank") } : undefined,
          });
          qc.invalidateQueries({ queryKey: ["invoices"] });
          setParams({}, { replace: true });
          return;
        }
        if (["failed", "expired"].includes(s?.payment_status) || s?.status === "failed") {
          sessionStorage.removeItem("fhapas_pending_invoice");
          setParams({}, { replace: true });
          toast.error("Payment failed", { description: "Your card was declined. This invoice is still unpaid.", action: retry });
          return;
        }
      } catch (_) {}
      if (tries < 8) setTimeout(poll, 2000);
      else { setParams({}, { replace: true }); toast.error("We couldn't confirm your payment", { description: "If you were charged it will update shortly; otherwise this invoice remains unpaid.", action: retry }); }
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const pName = (id) => patients.find((p) => p.id === id)?.full_name || "—";

  const printInvoice = (inv) => {
    const items = inv.items || [];
    const rows = items.map((i) => `<tr><td style="padding:6px 0">${(i.description || "").replace(/</g, "&lt;")}</td><td style="text-align:right">${money(i.amount, cur)}</td></tr>`).join("") || `<tr><td style="padding:6px 0;color:#94a3b8">No line items</td><td></td></tr>`;
    const brand = org?.brand_color || "#0F766E";
    const logo = org?.logo ? `<img src="${logoSrc(org.logo)}" style="height:48px;object-fit:contain" crossorigin="anonymous"/>` : "";
    const html = `<html><head><title>Invoice ${inv.invoice_number || inv.id}</title></head>
      <body style="font-family:Arial,Helvetica,sans-serif;padding:36px;color:#0f172a">
      <div style="display:flex;justify-content:space-between;align-items:flex-end;border-bottom:3px solid ${brand};padding-bottom:16px">
        <div>${logo}<h2 style="margin:8px 0 2px;color:${brand}">${(org?.name || "FHAPAS").replace(/</g, "&lt;")}</h2>${profile.address ? `<p style="margin:0;color:#64748b;font-size:12px;max-width:260px">${profile.address.replace(/</g, "&lt;")}</p>` : ""}${profile.tax_number ? `<p style="margin:2px 0 0;color:#64748b;font-size:12px">TIN: ${profile.tax_number.replace(/</g, "&lt;")}</p>` : ""}</div>
        <div style="text-align:right"><h1 style="margin:0;letter-spacing:1px">INVOICE</h1><p style="margin:4px 0;color:#64748b">${inv.invoice_number || inv.id}</p></div>
      </div>
      <p style="margin-top:18px">Bill to: <b>${(pName(inv.patient_id) || inv.patient_name || "—").replace(/</g, "&lt;")}</b><br/>Status: ${(inv.status || "unpaid").toUpperCase()}</p>
      <table style="width:100%;border-collapse:collapse;margin-top:14px">
        <thead><tr style="border-bottom:1px solid #e2e8f0"><th style="text-align:left;padding:6px 0">Description</th><th style="text-align:right">Amount</th></tr></thead>
        <tbody>${rows}</tbody>
        <tfoot><tr style="border-top:2px solid ${brand}"><td style="padding-top:12px;font-weight:700">Total</td><td style="text-align:right;padding-top:12px;font-weight:700">${money(inv.total, cur)}</td></tr></tfoot>
      </table>
      ${profile.payment_terms ? `<p style="margin-top:18px;color:#334155;font-size:13px"><b>Payment terms:</b> ${profile.payment_terms.replace(/</g, "&lt;")}</p>` : ""}
      <p style="margin-top:36px;color:#94a3b8;font-size:12px">Generated by FHAPAS · fhapas.com</p>
      <script>window.onload=function(){setTimeout(function(){window.print()},400)}<\/script>
      </body></html>`;
    const w = window.open("", "_blank");
    if (w) { w.document.write(html); w.document.close(); } else { toast.error("Allow pop-ups to download the invoice PDF"); }
  };

  const columns = [
    { key: "invoice_number", label: "Invoice", render: (r) => <span className="font-mono text-slate-700">{r.invoice_number || r.id?.slice(0, 10)}</span> },
    { key: "patient", label: "Patient", render: (r) => r.patient_name || pName(r.patient_id) },
    { key: "total", label: "Total", render: (r) => <span className="font-mono">{money(r.total, cur)}</span> },
    { key: "status", label: "Status", type: "badge" },
    { key: "created_at", label: "Date", render: (r) => fmtDate(r.created_at) },
    { key: "actions", label: "", render: (r) => (
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" className="gap-1 text-slate-600" data-testid={`pdf-${r.id}`} onClick={() => printInvoice(r)}><FileText className="h-3.5 w-3.5" /> PDF</Button>
        {r.status !== "paid" && <Button size="sm" variant="outline" className="gap-1 border-teal-200 text-teal-700" data-testid={`pay-${r.id}`} onClick={() => checkoutMut.mutate(r.id)}><CreditCard className="h-3.5 w-3.5" /> Pay</Button>}
      </div>
    )},
  ];

  return (
    <PaywallGate>
      <PageHeader title="Billing & Invoices" subtitle={`${invoices.length} invoices`} testId="page-billing">
        <Button data-testid="add-invoice" className="gap-1.5 bg-teal-700 hover:bg-teal-800" onClick={() => setOpen(true)}><Plus className="h-4 w-4" /> New invoice</Button>
      </PageHeader>

      <DataTable columns={columns} rows={invoices} loading={isLoading} currency={cur}
        emptyTitle="No invoices yet" emptyDescription="Create an invoice for a patient." emptyAction="New invoice" onEmptyAction={() => setOpen(true)} testId="table-invoices" />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-display">New invoice</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label>Patient</Label>
              <Select value={patientId} onValueChange={setPatientId}>
                <SelectTrigger data-testid="invoice-patient"><SelectValue placeholder="Select patient" /></SelectTrigger>
                <SelectContent>{patients.map((p) => <SelectItem key={p.id} value={p.id}>{p.full_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Line items</Label>
              {items.map((it, idx) => (
                <div key={idx} className="flex gap-2">
                  <Input data-testid={`item-desc-${idx}`} placeholder="Description" value={it.description} onChange={(e) => setItems((s) => s.map((x, i) => i === idx ? { ...x, description: e.target.value } : x))} />
                  <Input data-testid={`item-amount-${idx}`} type="number" placeholder="0" value={it.amount} onChange={(e) => setItems((s) => s.map((x, i) => i === idx ? { ...x, amount: e.target.value } : x))} className="w-28" />
                  {items.length > 1 && <Button variant="ghost" size="icon" onClick={() => setItems((s) => s.filter((_, i) => i !== idx))}><Trash2 className="h-4 w-4 text-rose-500" /></Button>}
                </div>
              ))}
              <Button variant="outline" size="sm" data-testid="add-line-item" onClick={() => setItems((s) => [...s, { description: "", amount: "" }])}><Plus className="mr-1 h-4 w-4" /> Add item</Button>
            </div>
            <div className="flex items-center justify-between rounded-lg bg-slate-50 px-4 py-3"><span className="text-sm font-medium text-slate-600">Total</span><span className="font-display text-lg font-bold text-slate-900">{money(total, cur)}</span></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="invoice-save" disabled={!patientId || createMut.isPending} className="bg-teal-700 hover:bg-teal-800" onClick={() => createMut.mutate()}>{createMut.isPending ? "Creating…" : "Create invoice"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PaywallGate>
  );
}
